import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import type { ClipFilters } from './clip.types'
import type { ClipCategory } from '../clip-categories/clip-category.types'

type Props = {
  filters: ClipFilters
  total: number
  categories: ClipCategory[]
  onFiltersChange: (filters: ClipFilters) => void
  onAddClick: () => void
}

const STATUS_OPTIONS = [
  { label: 'ทุกสถานะ', value: 'all' },
  { label: 'พร้อม', value: 'ready' },
  { label: 'กำลังประมวลผล', value: 'processing' },
  { label: 'ล้มเหลว', value: 'failed' },
] as const

const PUBLISH_OPTIONS = [
  { label: 'ทั้งหมด', value: 'all' },
  { label: 'เผยแพร่แล้ว', value: 'true' },
  { label: 'ซ่อน', value: 'false' },
] as const

export default function ClipsFilter({
  filters,
  total,
  categories,
  onFiltersChange,
  onAddClick,
}: Props) {
  // Local mirror of the search box so typing stays responsive; the committed
  // value is debounced out to the parent (which refetches) to avoid one request
  // per keystroke.
  const [searchInput, setSearchInput] = useState(filters.search)

  useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  useEffect(() => {
    if (searchInput === filters.search) return
    const timer = setTimeout(() => {
      updateFilters({ search: searchInput })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Any filter change returns to page 1 so results stay consistent.
  const updateFilters = (updates: Partial<ClipFilters>) => {
    onFiltersChange({ ...filters, ...updates, page: 1 })
  }

  const clearFilters = () => {
    setSearchInput('')
    onFiltersChange({
      ...filters,
      page: 1,
      category: '',
      status: '',
      isPublished: 'all',
      search: '',
    })
  }

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold text-black">จัดการคลิป</h2>
              <p className="text-sm text-muted-foreground">{total} คลิป</p>
            </div>
            <Button size="sm" onClick={onAddClick}>
              <Plus className="h-4 w-4 mr-1" />
              เพิ่มคลิป
            </Button>
          </div>

          <Separator />

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">ค้นหาชื่อคลิป</label>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="พิมพ์ชื่อคลิป..."
                className="w-[220px]"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">หมวดหมู่</label>
              <Select
                value={filters.category || 'all'}
                onValueChange={(value) =>
                  updateFilters({ category: value === 'all' ? '' : value })
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="ทุกหมวดหมู่" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกหมวดหมู่</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">สถานะการประมวลผล</label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) =>
                  updateFilters({ status: value === 'all' ? '' : value })
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="ทุกสถานะ" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">การเผยแพร่</label>
              <Select
                value={filters.isPublished}
                onValueChange={(value) =>
                  updateFilters({
                    isPublished: value as ClipFilters['isPublished'],
                  })
                }
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="ทั้งหมด" />
                </SelectTrigger>
                <SelectContent>
                  {PUBLISH_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" onClick={clearFilters}>
              ล้างตัวกรอง
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
